import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Chess } from "chess.js";
//import { io } from "socket.io-client";
import { AuthContext } from "../App";
import GameInfoPanel from './GameInfoPanel';
import ChessGameLayout from './ChessGameLayout';
import { useTheme, ThemedButton } from './ThemeContext';
import chessPieces from './chesspieces';
import ReactDOMServer from 'react-dom/server';
import { useSound } from './SoundManager';
import { createGameSocket, isLocalGameId } from './GameSocketProvider';
import WaitingGameSuggestion from './WaitingGameSuggestion';
//import SoundTestComponent from "./SoundTestComponent";


const slotColors = {
  0: {
    base: "rgb(59, 130, 246)",
    glow: "drop-shadow(0 0 6px rgb(59, 130, 246)) drop-shadow(0 0 12px rgb(59, 130, 246))"
  },
  1: {
    base: "rgb(34, 197, 94)",
    glow: "drop-shadow(0 0 6px rgb(34, 197, 94)) drop-shadow(0 0 12px rgb(34, 197, 94))"
  },
  2: {
    base: "rgb(168, 85, 247)",
    glow: "drop-shadow(0 0 6px rgb(168, 85, 247)) drop-shadow(0 0 12px rgb(168, 85, 247))"
  }
};

const ChessGame = () => {
  const { woodStyles } = useTheme();
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { playSound, stopSound, playWithDelay, playSequence, isMuted } = useSound();
  const [showWaitingSuggestion, setShowWaitingSuggestion] = useState(true);

  // Core Chessgame state
  const chessRef = useRef(null);
  if (!chessRef.current) {
    chessRef.current = new Chess();
  }

  // Game state management
  const [gameSocket, setGameSocket] = useState(null);
  const [globalState, setGlobalState] = useState(null);
  const [error, setError] = useState(null);
  const [playerConnections, setPlayerConnections] = useState({
    white: true,
    black: true
  });
  const gameStatusRef = useRef(null);
  const [isStatusVisible, setIsStatusVisible] = useState(true);
  const [showNotification, setShowNotification] = useState(false);

  // Board UI state
  const [boardOrientation, setBoardOrientation] = useState("white");
  const [rightClickedSquares, setRightClickedSquares] = useState({});
  const [moveSquares, setMoveSquares] = useState({});
  const [optionSquares, setOptionSquares] = useState({});
  const [moveAnimationDuration, setMoveAnimationDuration] = useState(150);

  // Timer state
  const [whiteTimer, setWhiteTimer] = useState(null);
  const [blackTimer, setBlackTimer] = useState(null);
  const timerIntervalRef = useRef(null);
  const currentTimerValue = useRef(null);

  // Computed state
  const playerColor = (globalState?.players?.white.id === user.id ? "white" : (globalState?.players?.black.id === user.id ? "black" : null));
  const isSpectator = !playerColor;
  const currentTurn = globalState?.turn === "w" ? "white" : "black";
  const userCanMove = !isSpectator && globalState?.status === "active" && currentTurn === playerColor;

  // Slot machine state
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [whiteSlots, setWhiteSlots] = useState(Array(3).fill(null));
  const [blackSlots, setBlackSlots] = useState(Array(3).fill(null));
  const whiteSlotsRef = useRef(null);
  const blackSlotsRef = useRef(null);
  const [isWhiteSpinning, setIsWhiteSpinning] = useState(false);
  const [isBlackSpinning, setIsBlackSpinning] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  //const [isSpinning, setIsSpinning] = useState(false);
  const [showGoldenBorder, setShowGoldenBorder] = useState(false);
  const [anarchyMessage, setAnarchyMessage] = useState(null);
  const [forcedMoveMessage, setForcedMoveMessage] = useState(null);
  const [finMoves, setFinMoves] = useState({ white: null, black: null });
  const [customArrows, setCustomArrows] = useState([]);
  const [uniqueMoveLimit, setUniqueMoveLimit] = useState(3);

  // Draw and regular offer state handling
  const [drawOffer, setDrawOffer] = useState(null);
  const [regularOffer, setRegularOffer] = useState(null);
  const [isRegularGame, setIsRegularGame] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [canClaimDraw, setCanClaimDraw] = useState(false);

  // Promotion Handling
  const [moveFrom, setMoveFrom] = useState("");
  const [moveTo, setMoveTo] = useState(null);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [isDragDropPromotion, setIsDragDropPromotion] = useState(false);

  // MoveHistory handling
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1);
  const viewingRef = useRef(null);
  const [showUnselectedMoves, setShowUnselectedMoves] = useState(false);
  // Socket setup

  useEffect(() => {
    //console.log('Setting up game socket for', gameId);
    const socket = createGameSocket(gameId, user);

    // Only for network sockets, not needed for local
    if (!socket.isLocal) {
      socket.getSocket().on("connect_error", (error) => {
        if (error.message === "Authentication required") {
          setError("Authentication required");
          navigate("/lobby");
        }
      });
    }

    setGameSocket(socket);
    //console.log(socket,gameSocket);
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [navigate, gameId, user]);

  const handleSwitchToBotGame = useCallback((game) => {
    // If the network game is in waiting status, abort it
    if (game.status === 'waiting' && gameSocket) {
      gameSocket.emit("abort_game", { gameId: game.id });
    }

    // Navigate to the lobby with a query parameter to open the bot modal
    navigate("/lobby?openBotModal=true");
  }, [navigate, gameSocket]);

  const handlePositionChange = useCallback((index) => {
    if (!viewingRef.current) {
      viewingRef.current = new Chess();
    }

    // Get previous position index to determine sound type
    const prevIndex = currentMoveIndex;

    // Only update timers during review if game is not active
    const isGameCompleted = globalState?.status !== 'active';

    // Reset the viewing board to starting position
    if (index === -1) {
      viewingRef.current = new Chess();
      setMoveSquares({});
      setCustomArrows([]);

      // Update timers to initial state only for completed games
      if (isGameCompleted && globalState?.playerTimes) {
        setWhiteTimer(globalState.timeControl || globalState.playerTimes.white);
        setBlackTimer(globalState.timeControl || globalState.playerTimes.black);
      }

      // No sound for resetting to starting position
    } else {
      // Reset and replay moves up to the selected index
      viewingRef.current = new Chess();

      // Only play sound for the last move we're navigating to
      let moveToPlay = null;

      for (let i = 0; i <= index; i++) {
        const moveObj = globalState.moves[i];
        viewingRef.current.move(moveObj);
        if (i === index) {
          moveToPlay = moveObj;
        }
      }

      // Update clock times based on the selected move - only for completed games
      if (isGameCompleted && moveToPlay) {
        // Set the clock times based on timeRemaining property in the move object
        const moveColor = moveToPlay.color === 'w' ? 'white' : 'black';
        const opponentColor = moveColor === 'white' ? 'black' : 'white';

        // Update clock for the player who just moved
        if (moveToPlay.timeRemaining !== undefined) {
          if (moveColor === 'white') {
            setWhiteTimer(moveToPlay.timeRemaining);
          } else {
            setBlackTimer(moveToPlay.timeRemaining);
          }
        }

        // For the opponent's clock, we need to look ahead to their next move
        // if available, otherwise show their starting time
        const nextMoveIndex = index + 1;
        if (nextMoveIndex < globalState.moves.length) {
          const nextMove = globalState.moves[nextMoveIndex];
          if (nextMove.timeRemaining !== undefined) {
            if (opponentColor === 'white') {
              setWhiteTimer(nextMove.timeRemaining);
            } else {
              setBlackTimer(nextMove.timeRemaining);
            }
          }
        } else {
          // We're at the last move, show current times from globalState
          if (opponentColor === 'white' && globalState.playerTimes?.white !== undefined) {
            setWhiteTimer(globalState.playerTimes.white);
          } else if (opponentColor === 'black' && globalState.playerTimes?.black !== undefined) {
            setBlackTimer(globalState.playerTimes.black);
          }
        }
      }

      // Play appropriate sound based on move type and direction
      if (moveToPlay && index !== prevIndex) {
        // Only play sounds when actually changing positions, not just re-rendering
        // Determine if we're moving forward or backward in the game
        if (window.HTMLAudioElement) {
          Object.keys(window.HTMLAudioElement).forEach(soundName => {
            stopSound(soundName);
          });
        }
        const isForward = prevIndex === -1 || index > prevIndex;

        if (isForward) {
          try {
            // Get the correct sound name based on move type with precedence
            let soundToPlay = 'MOVE';

            // Check > Capture > Promotion > Castle > Regular Move
            if (moveToPlay.san?.includes('#')) {
              soundToPlay = 'CHECKMATE';
            }
            else if (moveToPlay.san?.includes('+')) {
              soundToPlay = 'CHECK';
            } else if (moveToPlay.captured) {
              soundToPlay = 'CAPTURE';
            } else if (moveToPlay.promotion) {
              soundToPlay = 'PROMOTION';
            } else if (moveToPlay.san?.includes('O-O')) {
              soundToPlay = 'CASTLE';
            }

            playSound(soundToPlay);

          } catch (e) {
            console.error("Error playing sound:", e);
          }
        } else {
          playSound('MOVE');

        }
      }

      // Update move squares to highlight the selected move and show all slot moves
      const selectedMove = globalState.moves[index];
      const newSquares = {};
      const arrows = [];

      // Handle slot moves if present
      if (selectedMove.slotMoves && selectedMove.slotMoves.length > 1) {
        // Regular highlighting for the played move should always be shown
        newSquares[selectedMove.from] = { backgroundColor: "rgba(255, 255, 0, 0.4)" };
        newSquares[selectedMove.to] = { backgroundColor: "rgba(255, 255, 0, 0.4)" };

        // Only add ghost pieces and additional arrows if showing unselected moves
        if (showUnselectedMoves) {
          selectedMove.slotMoves.forEach((move, slotIndex) => {
            if (move.san !== selectedMove.san) {
              const pieceKey = `${move.color === 'w' ? 'w' : 'b'}${move.promotion ? move.promotion.toUpperCase() : move.piece.toUpperCase()}`;
              const pieceSvg = React.cloneElement(chessPieces[pieceKey], {
                style: {
                  ...chessPieces[pieceKey].props.style,
                  stroke: 'none'
                }
              });

              const svgString = encodeURIComponent(ReactDOMServer.renderToString(pieceSvg));

              // If target square is the same as selected move, adjust position
              const shouldOffset = move.to === selectedMove.to;
              const position = shouldOffset ?
                (slotIndex === 0 ? '15% center' : slotIndex === 1 ? 'center center' : '85% center') :
                'center';

              const currentStyle = newSquares[move.to] || {};
              newSquares[move.to] = {
                ...currentStyle,
                backgroundImage: `${currentStyle.backgroundImage ? currentStyle.backgroundImage + ', ' : ''}url("data:image/svg+xml,${svgString}")`,
                backgroundSize: '45%',
                backgroundPosition: position,
                backgroundRepeat: 'no-repeat',
                opacity: 0.6,
                filter: slotColors[slotIndex].glow
              };
            }
          });

          // Add arrows for all moves when showing unselected
          selectedMove.slotMoves.forEach((move, slotIndex) => {
            arrows.push({
              from: move.from,
              to: move.to,
              color: slotColors[slotIndex].base,
              opacity: move.san === selectedMove.san ? 0.8 : 0.4,
              width: move.san === selectedMove.san ? 8 : 4
            });
          });
        } else {
          // When not showing unselected moves, only add arrow for the selected move
          const selectedMoveIndex = selectedMove.slotMoves.findIndex(m => m.san === selectedMove.san);
          arrows.push({
            from: selectedMove.from,
            to: selectedMove.to,
            color: slotColors[selectedMoveIndex >= 0 ? selectedMoveIndex : 0].base,
            opacity: 0.8,
            width: 8
          });
        }
      } else {
        // Regular move highlighting for regular games
        newSquares[selectedMove.from] = { backgroundColor: "rgba(255, 255, 0, 0.4)" };
        newSquares[selectedMove.to] = { backgroundColor: "rgba(255, 255, 0, 0.4)" };
      }

      setMoveSquares(newSquares);
      setCustomArrows(arrows);
    }
    setCurrentMoveIndex(index);
  }, [globalState?.moves, globalState?.status, globalState?.playerTimes, globalState?.timeControl,
    showUnselectedMoves, currentMoveIndex, stopSound, playSound, playSequence]);


  // Move calculation helpers
  const calculateMoves = useCallback((square) => {
    const moves = [];
    const piece = chessRef.current.get(square);
    if (piece && piece.color === chessRef.current.turn()) {
      const possibleMoves = chessRef.current.moves({
        square,
        verbose: true,
      });
      return possibleMoves
        .map((move) => {
          if (move.flags?.includes("p")) {
            // Make a temporary move to get the correct san with check/mate
            const testGame = new Chess(chessRef.current.fen());
            return ["q", "r", "b", "n"].map((promotion) => {
              const testMove = testGame.move({
                from: move.from,
                to: move.to,
                promotion
              });
              testGame.undo();
              return {
                ...move,
                promotion,
                san: testMove.san,
                fromSquare: square,
              };
            });
          }
          return {
            ...move,
            fromSquare: square,
          };
        })
        .flat();
    }
    return moves;
  }, []);

  // Board interaction handlers

  const onSquareRightClick = useCallback((square) => {
    const colour = "rgba(0, 0, 255, 0.4)";
    setRightClickedSquares((prev) => ({
      ...prev,
      [square]: prev[square]?.backgroundColor === colour
        ? undefined
        : { backgroundColor: colour },
    }));
  }, []);



  const updateBoardVisualization = useCallback((moves) => {
    const uniqueMovesSet = new Set();
    const validMoves = moves.map(move => {
      if (move && !uniqueMovesSet.has(move.san)) {
        uniqueMovesSet.add(move.san);
        return move;
      }
      return null;
    });

    // Create new squares object starting with last move
    const arrows = [];
    const newSquareStyles = {};
    if (globalState?.lastMove) {
      newSquareStyles[globalState.lastMove.from] = {
        backgroundColor: "rgba(255, 255, 0, 0.4)"
      };
      newSquareStyles[globalState.lastMove.to] = {
        backgroundColor: "rgba(255, 255, 0, 0.4)"
      };
    }

    const movesBySquare = validMoves.reduce((acc, move, index) => {
      if (move) {
        if (!acc[move.to]) {
          acc[move.to] = [];
        }
        acc[move.to].push({ move, index });
      }
      return acc;
    }, {});

    Object.entries(movesBySquare).forEach(([targetSquare, moves]) => {
      const pieceSvgs = moves.map(({ move, index }) => {
        const pieceKey = `${move.color === 'w' ? 'w' : 'b'}${move.promotion ? move.promotion.toUpperCase() : move.piece.toUpperCase()}`;
        const pieceSvg = React.cloneElement(chessPieces[pieceKey], {
          style: {
            ...chessPieces[pieceKey].props.style,
            stroke: 'none'
          }
        });

        const svgString = encodeURIComponent(ReactDOMServer.renderToString(pieceSvg));
        return {
          dataUrl: `url("data:image/svg+xml,${svgString}")`,
          index
        };
      });

      // Position pieces based on count
      const positions = pieceSvgs.map((_, i) => {
        if (pieceSvgs.length === 1) return 'center';
        if (pieceSvgs.length === 2) return i === 0 ? '15% center' : '85% center';
        if (pieceSvgs.length === 3) {
          if (i === 0) return 'center 15%';
          if (i === 1) return '15% 85%';
          return '85% 85%';
        }
        return 'center';
      });

      // Merge with existing styles if it's a last move square
      const existingStyle = newSquareStyles[targetSquare] || {};
      newSquareStyles[targetSquare] = {
        ...existingStyle,
        backgroundImage: pieceSvgs.map(p => p.dataUrl).join(', '),
        backgroundSize: pieceSvgs.map(() => pieceSvgs.length > 1 ? '45%' : '60%').join(', '),
        backgroundPosition: positions.join(', '),
        backgroundRepeat: 'no-repeat',
        opacity: 0.7,
        filter: pieceSvgs.map(p => slotColors[p.index].glow).join(' '),
      };

      moves.forEach(({ move, index }) => {
        arrows.push({
          from: move.from,
          to: move.to,
          color: slotColors[index].base,
          opacity: 0.8,
          width: 8
        });
      });
    });

    setMoveSquares(newSquareStyles);
    setCustomArrows(arrows);
    setOptionSquares({});
  }, [globalState?.lastMove]);

  const handleMoveSelected = useCallback((moves, isOpponentMove = false) => {
    const uniqueMovesSet = new Set();

    // First, if in anarchy mode, add the anarchy move to the set of used moves
    // This prevents selecting the same move for slots 0 or 1
    if (globalState?.isAnarchy) {
      const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;
      const anarchyMove = currentSlots[2];

      if (anarchyMove) {
        const anarchyMoveKey = anarchyMove.san + anarchyMove.from + anarchyMove.to;
        uniqueMovesSet.add(anarchyMoveKey);
      }
    }

    // Now process the moves, respecting already used moves
    const validMoves = moves.map((move, index) => {
      // Keep anarchy move in slot 2 untouched
      if (globalState?.isAnarchy && index === 2) {
        const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;
        return currentSlots[2];
      }

      if (move) {
        const uniqueKey = move.san + move.from + move.to;

        if (!uniqueMovesSet.has(uniqueKey)) {
          uniqueMovesSet.add(uniqueKey);
          return move;
        }
        return null; // Skip duplicate moves
      }
      return move;
    });

    //console.log("Trying to select", validMoves);

    if (userCanMove && !isOpponentMove) {
      gameSocket?.emit('update_slot_moves', {
        gameId,
        moves: validMoves, // Send only unique moves
        color: playerColor
      });
    }

    updateBoardVisualization(validMoves);
    setIsDragDropPromotion(false);
    setShowPromotionDialog(false);

    // Update refs to match the new moves
    if (playerColor === 'white') {
      whiteSlotsRef.current = validMoves;
    } else {
      blackSlotsRef.current = validMoves;
    }

    return validMoves;
  }, [userCanMove, gameSocket, gameId, playerColor, updateBoardVisualization, blackSlots, whiteSlots, globalState?.isAnarchy]);

  const handleRandomMove = useCallback(() => {
    if (!userCanMove) return;
    const allMoves = chessRef.current.moves({ verbose: true });
    if (allMoves.length > 0) {
      // Get current slots based on player color
      const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;
      const setSlots = playerColor === 'white' ? setWhiteSlots : setBlackSlots;

      // Filter out moves that are already selected
      const usedMoves = new Set(
        currentSlots.filter(Boolean).map((m) => m.san)
      );
      const availableMoves = allMoves.filter(
        (move) => !usedMoves.has(move.san)
      );

      if (availableMoves.length > 0) {
        const randomMove = availableMoves[
          Math.floor(Math.random() * availableMoves.length)
        ];

        // Use selected slot or find first empty slot
        const targetSlot =
          selectedSlot !== null
            ? selectedSlot
            : currentSlots.findIndex((m) => !m);

        if (targetSlot !== -1 && (!globalState?.isAnarchy || targetSlot !== 2)) {
          const newMoves = [...currentSlots];
          newMoves[targetSlot] = {
            ...randomMove,
            color: chessRef.current.turn(),
            piece: chessRef.current.get(randomMove.from).type,
          };

          // Emit to server instead of directly setting state
          gameSocket?.emit('update_slot_moves', {
            gameId,
            moves: newMoves,
            color: playerColor
          });

          setSelectedSlot(null);
        }
      }
    }
  }, [
    userCanMove,
    whiteSlots,
    blackSlots,
    gameSocket,
    gameId,
    playerColor,
    selectedSlot,
    globalState?.isAnarchy,
  ]);
  // Handle move submission
  const handleSubmitMoves = useCallback(async () => {

    if (!userCanMove || !gameSocket || isRegularGame) return;

    // Stop local timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }



    // Submit moves to server - server will handle selection
    gameSocket.emit('submit_slot_moves', {
      gameId,
      color: playerColor
    });

    // Clear UI states
    setOptionSquares({});
    setSelectedPiece(null);
    setCustomArrows([]);
  }, [gameId, gameSocket, userCanMove, playerColor, isRegularGame]);

  //Click and drop Handling integrated with both chess and slot functionality
  const onPieceDrop = useCallback(
    (sourceSquare, targetSquare, piece) => {
      const isSpinning = playerColor === 'white' ? isWhiteSpinning : isBlackSpinning;

      if (!userCanMove || isSpinning) return false;

      if (isRegularGame) {
        // Regular chess logic remains unchanged
        const moves = calculateMoves(sourceSquare);
        const validMove = moves.find(m => m.to === targetSquare);

        if (!validMove) return false;

        if (validMove.flags?.includes('p')) {
          setIsDragDropPromotion(true);
          setMoveFrom(sourceSquare);
          setMoveTo(targetSquare);
          setShowPromotionDialog(true);
          return false;
        }

        setMoveAnimationDuration(0);
        gameSocket?.emit("make_move", {
          gameId,
          move: {
            from: sourceSquare,
            to: targetSquare
          }
        });
        return false;
      }

      // Slot chess logic
      const moves = calculateMoves(sourceSquare);
      const move = moves.find((m) => m.to === targetSquare);

      if (move) {
        if (move.flags?.includes('p')) {
          setIsDragDropPromotion(true);
          setMoveFrom(sourceSquare);
          setMoveTo(targetSquare);
          setShowPromotionDialog(true);
          return false;
        }

        const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;
        const targetSlot = selectedSlot !== null
          ? selectedSlot
          : currentSlots.findIndex((m) => !m);

        if (targetSlot !== -1 && (!globalState?.isAnarchy || targetSlot !== 2)) {
          const newMove = {
            ...move,
            color: chessRef.current.turn(),
            piece: piece[1].toLowerCase(),
            san: move.san,
          };

          const newMoves = [...currentSlots];
          newMoves[targetSlot] = newMove;

          // Emit to server instead of direct state update
          gameSocket?.emit('update_slot_moves', {
            gameId,
            moves: newMoves,
            color: playerColor
          });

          setSelectedSlot(null);
        }
      }

      return false;
    },
    [
      userCanMove,
      isWhiteSpinning,
      isBlackSpinning,
      calculateMoves,
      whiteSlots,
      blackSlots,
      selectedSlot,
      gameSocket,
      gameId,
      playerColor,
      isRegularGame,
      globalState?.isAnarchy
    ]
  );

  //promotion piece selection handler 
  const onPromotionPieceSelect = useCallback(
    (piece, from, to) => {
      if (!piece) {
        setMoveFrom("");
        setMoveTo(null);
        setShowPromotionDialog(false);
        setIsDragDropPromotion(false);
        setOptionSquares({});
        return true;
      }

      const sourceSquare = from || moveFrom;
      const targetSquare = to || moveTo;

      if (isRegularGame) {
        // Regular chess promotion remains unchanged
        gameSocket?.emit("make_move", {
          gameId,
          move: {
            from: sourceSquare,
            to: targetSquare,
            promotion: piece[1].toLowerCase()
          },
        });
        setShowPromotionDialog(false);
        return true;
      }

      const moves = calculateMoves(sourceSquare);
      const move = moves.find(
        (m) => m.to === targetSquare && m.promotion === piece[1].toLowerCase()
      );

      if (move) {
        const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;
        const targetSlot = selectedSlot !== null
          ? selectedSlot
          : currentSlots.findIndex((m) => !m);

        if (targetSlot !== -1 && (!globalState?.isAnarchy || targetSlot !== 2)) {
          const newMove = {
            ...move,
            color: chessRef.current.turn(),
            piece: 'p',
            promotion: piece[1].toLowerCase(),
            san: move.san
          };

          const newMoves = [...currentSlots];
          newMoves[targetSlot] = newMove;

          // Emit to server instead of direct state update
          gameSocket?.emit('update_slot_moves', {
            gameId,
            moves: newMoves,
            color: playerColor
          });
        }
      }

      setIsDragDropPromotion(false);
      setShowPromotionDialog(false);
      return true;
    },
    [
      moveFrom,
      moveTo,
      calculateMoves,
      selectedSlot,
      whiteSlots,
      blackSlots,
      gameSocket,
      gameId,
      playerColor,
      globalState?.isAnarchy,
      isRegularGame
    ]
  );

  const onSquareClick = useCallback((square) => {
    const isSpinning = playerColor === 'white' ? isWhiteSpinning : isBlackSpinning;
    if (!userCanMove || (isSpinning && !isRegularGame)) return;

    setRightClickedSquares({});
    const piece = chessRef.current.get(square);

    if (isRegularGame) {
      setMoveAnimationDuration(150);

      if (piece && piece.color === chessRef.current.turn()) {
        // Selecting a piece
        setSelectedPiece(square);
        const moves = calculateMoves(square);

        const newSquares = {};
        moves.forEach((move) => {
          newSquares[move.to] = chessRef.current.get(move.to)
            ? { background: `radial-gradient(circle, rgba(0,0,0,.1) 85%, transparent 85%)` }
            : { background: `radial-gradient(circle, rgba(0,0,0,.1) 25%, transparent 25%)` };
        });
        setOptionSquares(newSquares);
      } else if (selectedPiece) {
        // Attempting to move
        const moves = calculateMoves(selectedPiece);
        const validMove = moves.find(m => m.to === square);

        if (validMove) {
          if (validMove.flags?.includes('p')) {
            setMoveFrom(selectedPiece);
            setMoveTo(square);
            setIsDragDropPromotion(false);
            setShowPromotionDialog(true);
          } else {
            gameSocket?.emit("make_move", {
              gameId,
              move: {
                from: selectedPiece,
                to: square
              }
            });
          }
        }
        setSelectedPiece(null);
        setOptionSquares({});
      }
      return;
    }

    // Slot chess logic
    const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;

    // If clicking a piece of our color
    if (piece && piece.color === chessRef.current.turn()) {
      setSelectedPiece(square);
      const moves = calculateMoves(square);

      // Show possible move squares with appropriate colors
      const newSquares = {};
      moves.forEach((move) => {
        const targetSlot = selectedSlot !== null
          ? selectedSlot
          : currentSlots.findIndex((m) => !m);

        if (targetSlot !== -1 && (!globalState?.isAnarchy || targetSlot !== 2)) {
          const slotColor = `rgba(${targetSlot === 0
              ? "59, 130, 246"
              : targetSlot === 1
                ? "34, 197, 94"
                : "168, 85, 247"
            }, 0.5)`;

          newSquares[move.to] = chessRef.current.get(move.to)
            ? { background: `radial-gradient(circle, ${slotColor} 85%, transparent 85%)` }
            : { background: `radial-gradient(circle, ${slotColor} 25%, transparent 25%)` };
        }
      });

      setOptionSquares(newSquares);
      return;
    }

    // If we have a piece selected and click a target square
    if (selectedPiece) {
      const moves = calculateMoves(selectedPiece);
      const move = moves.find((m) => m.to === square);

      if (move) {
        const targetSlot = selectedSlot !== null
          ? selectedSlot
          : currentSlots.findIndex((m) => !m);

        if (targetSlot !== -1 && (!globalState?.isAnarchy || targetSlot !== 2)) {
          if (move.flags?.includes('p')) {
            setMoveFrom(selectedPiece);
            setMoveTo(square);
            setIsDragDropPromotion(false);
            setShowPromotionDialog(true);
          } else {
            const newMove = {
              ...move,
              color: chessRef.current.turn(),
              piece: chessRef.current.get(selectedPiece).type,
            };

            const newMoves = [...currentSlots];
            newMoves[targetSlot] = newMove;

            handleMoveSelected(newMoves);

          }
        }
      }

      setSelectedPiece(null);
      setOptionSquares({});
      setSelectedSlot(null);
    }
  }, [
    userCanMove,
    isWhiteSpinning,
    isBlackSpinning,
    calculateMoves,
    selectedPiece,
    whiteSlots,
    blackSlots,
    selectedSlot,
    gameSocket,
    gameId,
    playerColor,
    isRegularGame,
    globalState?.isAnarchy,
    handleMoveSelected
  ]);



  // Board initialization

  useEffect(() => {
    if (playerColor) {
      setBoardOrientation(playerColor);
    }

  }, [playerColor]);
  //Initializing slots on refresh
  useEffect(() => {
    if (globalState && globalState.slotPendingMoves) {

      if (globalState.isRegularGame) {
        setIsRegularGame(true);
        return;
      }

      // Update white slots if not already set
      if (!whiteSlotsRef.current || !blackSlotsRef.current) {
        const currentColor = globalState.turn === 'w' ? 'white' : 'black';
        //console.log("Loaded on refresh");
        const pendingWhiteMoves = globalState.slotPendingMoves.white || Array(3).fill(null);
        const pendingBlackMoves = globalState.slotPendingMoves.black || Array(3).fill(null);
        whiteSlotsRef.current = globalState.slotPendingMoves.white;
        setWhiteSlots(pendingWhiteMoves);
        blackSlotsRef.current = globalState.slotPendingMoves.white;
        setBlackSlots(pendingBlackMoves);

        if (currentColor === 'white')
          updateBoardVisualization(pendingWhiteMoves);
        if (currentColor === 'black')
          updateBoardVisualization(pendingBlackMoves);

      }
    }
  }, [globalState, playerColor, updateBoardVisualization]);


  // Move state handling - should respect current review state
  useEffect(() => {
    if (!globalState) return;

    if (globalState.lastMove?.isEnPassant && globalState.lastMove?.playerId === user.id && globalState.isAnarchy) {
      // Get a random anarchy message
      const anarchyMessages = [
        "Holy Hell!",
        "Google En Passant",
        "Actual Zombie",
        "En Passant is forced!",
        "The sacred move has been played"
      ];
      const randomMessage = anarchyMessages[Math.floor(Math.random() * anarchyMessages.length)];

      // Set the anarchy message
      setAnarchyMessage({
        text: randomMessage,
        color: "bg-red-500"
      });

      // Clear the message after a few seconds
      setTimeout(() => {
        setAnarchyMessage(null);
      }, 5000);
    }
    // Only update finMove and showGoldenBorder
    if (globalState.lastMove?.playerId === user.id) {
      setShowGoldenBorder(globalState.status === 'active');
      setFinMoves(prev => ({
        ...prev,
        [globalState.turn === 'w' ? 'black' : 'white']: globalState.lastMove
      }));
    } else if (globalState.status !== 'active' && globalState.moves) {
      const moveLength = globalState.moves.length;
      const LastMoveMinusOne = globalState.moves[moveLength - 2];
      if (LastMoveMinusOne?.playerId === user.id) {
        setFinMoves(prev => ({
          ...prev,
          [LastMoveMinusOne.color === 'w' ? 'white' : 'black']: LastMoveMinusOne
        }));
      }
    }
  }, [user.id, globalState]);

  useEffect(() => {
    // Re-trigger handlePositionChange for current position when toggle changes
    if (currentMoveIndex !== -1) {
      handlePositionChange(currentMoveIndex);
    }

  }, [showUnselectedMoves, handlePositionChange, currentMoveIndex]);
  // Effect to initialize currentMoveIndex when globalState is first loaded
  useEffect(() => {
    if (globalState?.moves?.length > 0 && currentMoveIndex === -1) {
      // Only initialize on first load or when no move is selected
      setCurrentMoveIndex(globalState.moves.length - 1);
    }
    if (globalState && !isSpectator) {
      if (globalState?.uniqueMoveLimit < 3 && globalState?.status === 'active' && globalState?.turn === playerColor[0]) {
        setUniqueMoveLimit(globalState.uniqueMoveLimit);
        setForcedMoveMessage(`Your spin choices are forced!`);
      }
      else {
        setForcedMoveMessage(null);
      }
    }
  }, [globalState, currentMoveIndex, showUnselectedMoves, playerColor, isSpectator]);





  const startTimerForColor = useCallback((color, initialTime) => {
    const setTimer = color === 'white' ? setWhiteTimer : setBlackTimer;

    // Clear existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Initialize both ref and state
    currentTimerValue.current = initialTime;
    setTimer(initialTime);

    // Start countdown using the ref
    timerIntervalRef.current = setInterval(() => {
      currentTimerValue.current = Math.max(0, currentTimerValue.current - 0.1);
      setTimer(currentTimerValue.current);

      if (currentTimerValue.current <= 0) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }, 100);
  }, []);

  useEffect(() => {
    // Handle game end or inactive state
    if (globalState?.status !== 'active') {
      // Clear all timers
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }


      // Set final times if game is over
      if (globalState?.status === 'completed' && globalState.playerTimes) {
        setWhiteTimer(globalState.playerTimes.white);
        setBlackTimer(globalState.playerTimes.black);
      }
      return;
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [globalState?.status, globalState?.playerTimes]);


  // Game state handler

  const handleGameState = useCallback(
    (state) => {
      if (!state) {
        console.error("Invalid state");

        return;
      }

      if (state.offers) {
        const pendingDrawOffer = state.offers.find(o => o.type === 'draw' && o.status === 'pending');
        const pendingRegularOffer = state.offers.find(o => o.type === 'regular_game' && o.status === 'pending');

        // Set or clear draw offer
        if (pendingDrawOffer) {
          setDrawOffer({ from: pendingDrawOffer.from });
        } else {
          setDrawOffer(null);
        }

        // Set or clear regular game offer
        if (pendingRegularOffer) {
          setRegularOffer({
            from: pendingRegularOffer.from,
            timeControl: pendingRegularOffer.timeControl
          });
        } else {
          setRegularOffer(null);
        }
      }



      const isNewMove = state.moves?.length !== globalState?.moves?.length;
      const isViewingLatest = currentMoveIndex === (globalState?.moves?.length - 1);
      const isInitialLoad = !globalState;

      if (state.lastMove) {
        const color = state.lastMove.color === 'w' ? 'white' : 'black';
        setFinMoves(prev => ({
          ...prev,
          [color]: state.lastMove
        }));

      }

      setGlobalState(state);


      // Update current move index when new moves are added
      // Only update move index if we're viewing the latest move
      //if (isInitialLoad || (isViewingLatest && isNewMove) || (isNewMove && currentMoveIndex === -1)) {
      setCurrentMoveIndex(state.moves?.length - 1);
      //  }
      // console.log(isInitialLoad || (isViewingLatest && isNewMove) || (isNewMove && currentMoveIndex === -1),isInitialLoad,isViewingLatest,isNewMove,currentMoveIndex);

      if (state.fen && chessRef.current.fen() !== state.fen) {
        try {
          chessRef.current.load(state.fen);

          if (!userCanMove) {
            setOptionSquares({});
            setShowGoldenBorder(false);
            setSelectedPiece(null);
            setSelectedSlot(null);
            setMoveSquares({});
          }



        } catch (err) {
          console.error("Invalid FEN string:", err);
          setError("Invalid game state received");
          return;
        }
      }

      // Update UI elements

      if (state.lastMove) {
        setMoveSquares({
          [state.lastMove.from]: {
            backgroundColor: "rgba(255, 255, 0, 0.4)",
          },

          [state.lastMove.to]: {
            backgroundColor: "rgba(255, 255, 0, 0.4)",
          },
        });
      }

      if (state.lastError) {

        setError(state.lastError);
      }

    },
    [userCanMove, currentMoveIndex, globalState, uniqueMoveLimit]
  );









  const scrollToStatus = useCallback(() => {
    gameStatusRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    setShowNotification(false);
  }, []);




  useEffect(() => {
    if (!gameSocket) {

      return;
    };

    const setupSocketHandlers = () => {

      //console.log("Entered setup"); 
      gameSocket.on("connect", () => {
        //console.log("Connecting to game", gameId);

        gameSocket.emit("connect_to_game", { gameId });
      });

      gameSocket.on('player_disconnected', ({ playerId }) => {
        setPlayerConnections(prev => {
          const color = playerId === globalState?.players?.white?.id ? 'white' : 'black';
          return { ...prev, [color]: false };
        });
      });

      gameSocket.on('player_reconnected', ({ playerId }) => {
        setPlayerConnections(prev => {
          const color = playerId === globalState?.players?.white?.id ? 'white' : 'black';
          return { ...prev, [color]: true };
        });
      });
      gameSocket.on("game_state", (state) => {

        if (state.status === 'active' && globalState?.status === 'waiting') {
          // Game just became active - play start sound
          playSound('GAME_START');
        }

        // rest of your existing code...
        handleGameState(state);
      });


      gameSocket.on('draw_offered', ({ from }) => {
        setDrawOffer({ from });
        playSound('NOTIFICATION');
      });

      gameSocket.on('draw_declined', () => {
        setDrawOffer(null);
        playSound('NOTIFICATION');
      });

      gameSocket.on('regular_game_offered', ({ from, timeControl }) => {
        playSound('NOTIFICATION');
        setRegularOffer({ from, timeControl });
      });

      gameSocket.on('regular_game_accepted', () => {

        setRegularOffer(null);
        setIsRegularGame(true);
        playSound('NOTIFICATION');
      });

      gameSocket.on('regular_game_declined', () => {

        setRegularOffer(null);
      });

      gameSocket.on('game_countdown', ({ count, initialTime }) => {
        setCountdownTimer(count);

        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }

        setWhiteTimer(initialTime);
        setBlackTimer(initialTime);

        // Reset UI elements
        setOptionSquares({});
        setCustomArrows([]);
        setMoveSquares({});
        setSelectedPiece(null);
        setIsWhiteSpinning(false);
        setIsBlackSpinning(false);
        setShowGoldenBorder(false);
        setCustomArrows([]);

        // Play countdown sounds
        if (count > 0) {
          playSound('COUNTDOWN_BEEP');
        } else if (count === 0) {
          playSound('COUNTDOWN_FINAL');
          setCountdownTimer(null);
        }
      });

      gameSocket.on("draw_claimable", ({ conditions }) => {

        setCanClaimDraw(conditions.canClaim);

        const reasons = [];

        if (conditions.fiftyMoveRule) reasons.push("fifty-move rule");

        if (conditions.repetition) reasons.push("threefold repetition");

        //setDrawClaimReasons(reasons);
      });

      gameSocket.on("game_error", (error) => {

        setError(error.message);
      });

      gameSocket.on('game_end', (result) => {
        if (result.globalState) {
          // Stop any ongoing sounds
          stopSound('SLOT_SPIN');

          // Stop timers
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }

          // Play appropriate end game sound based on result type and player perspective
          setTimeout(() => {
            if (result.type === 'checkmate') {
              // Determine if player won or lost (if not spectator)
              if (!isSpectator) {
                const isWinner = result.winner?.id === user.id;
                playSound(isWinner ? 'WIN' : 'LOSE');
              } else {
                // For spectators, play checkmate sound
                playSound('CHECKMATE');
              }
            } else if (result.type === 'timeout') {
              // Determine if player won or lost (if not spectator)
              if (!isSpectator) {
                const isWinner = result.winner?.id === user.id;
                playSound(isWinner ? 'WIN' : 'LOSE');
              } else {
                // For spectators, play generic sound
                playSound('NOTIFICATION');
              }
            } else if (result.type === 'resignation') {
              // Determine if player won or lost (if not spectator)
              if (!isSpectator) {
                const isWinner = result.winner?.id === user.id;
                playSound(isWinner ? 'WIN' : 'LOSE');
              } else {
                // For spectators, play generic sound
                playSound('NOTIFICATION');
              }
            } else if (
              result.type === 'draw' ||
              result.type === 'stalemate' ||
              result.type.includes('repetition') ||
              result.type.includes('fifty-move')
            ) {
              // Always play draw sound for draw results
              playSound('DRAW');
            }
          }, 500); // Delay to let move sound finish

          handleGameState(result.globalState);

          if (result.type === 'aborted') {
            setTimeout(() => navigate("/lobby"), 1000);
          }

          setRegularOffer(null);
          setDrawOffer(null);
        }
      });


      gameSocket.on('game_update', (state) => {
        if (state.status === 'active') {
          const currentColor = state.turn === 'w' ? 'white' : 'black';

          // Update displays
          setWhiteTimer(state.playerTimes.white);
          setBlackTimer(state.playerTimes.black);

          // Clear slots
          setWhiteSlots(Array(3).fill(null));
          setBlackSlots(Array(3).fill(null));
          whiteSlotsRef.current = null;
          blackSlotsRef.current = null;
          setMoveSquares({});

          // Play appropriate move sound if there's a new move
          if (state.lastMove && (!globalState || state.lastMove !== globalState.lastMove)) {
            let moveSound = 'MOVE';

            if (state.lastMove.san?.includes('#')) {
              moveSound = 'CHECKMATE';
            }
            else if (state.lastMove.san?.includes('+')) {
              moveSound = 'CHECK';
            }
            else if (state.lastMove.captured) {
              moveSound = 'CAPTURE';
            } else if (state.lastMove.san?.includes('O-O')) {
              moveSound = 'CASTLE';
            } else if (state.lastMove.promotion) {
              moveSound = 'PROMOTION';
            }

            playSound(moveSound);


          }
        }

        handleGameState(state);
      });


      gameSocket.on('time_update', ({ calculatedTime, activeColor, playerTimes, globalState }) => {
        const currentLocalTime = currentTimerValue.current;
        if (isWhiteSpinning || isBlackSpinning) return;
        if (globalState?.status === 'active') {
          if (currentLocalTime === null || !timerIntervalRef.current) {
            if (activeColor === 'white') {
              setWhiteTimer(calculatedTime);
              setBlackTimer(playerTimes.black);
              startTimerForColor('white', calculatedTime);
            } else {
              setBlackTimer(calculatedTime);
              setWhiteTimer(playerTimes.white);
              startTimerForColor('black', calculatedTime);
            }
          }
          else if (Math.abs(currentLocalTime - calculatedTime) >= 1) {
            //console.log('Time Drift with server time > 1s',currentLocalTime,calculatedTime);
            if (activeColor === 'white') {
              setWhiteTimer(calculatedTime);
              startTimerForColor('white', calculatedTime);
            } else {
              setBlackTimer(calculatedTime);
              startTimerForColor('black', calculatedTime);
            }
          }
        }
      });
      //Slot end points
      gameSocket.on('slot_moves_updated', ({ slotPendingMoves, anarchyMove, color, globalState }) => {
        // Ensure slotPendingMoves is an array and not undefined

        const pendingMoves = Array.isArray(slotPendingMoves) ? slotPendingMoves : [];

        // Get the previous slots state to compare
        const prevSlots = color === 'white' ? whiteSlots : blackSlots;

        // Count filled slots before and after update to determine sound
        const prevFilledCount = prevSlots.filter(Boolean).length;
        const newFilledCount = pendingMoves.filter(Boolean).length;

        // Play appropriate sound based on add/remove action
        if (newFilledCount > prevFilledCount) {
          playSound('MOVE');
        } else if (newFilledCount < prevFilledCount) {
          playSound('SLOT_REMOVE');
        }

        // Ensure anarchyMove is handled correctly in anarchy mode
        const newMoves = [...pendingMoves];
        if (globalState?.isAnarchy && anarchyMove) {

          newMoves[2] = anarchyMove;
        }

        // Update state
        setWhiteSlots(globalState.slotPendingMoves.white);
        setBlackSlots(globalState.slotPendingMoves.black);

        if (color === 'white') {
          whiteSlotsRef.current = newMoves;
        } else {
          blackSlotsRef.current = newMoves;
        }

        // Update board visualization
        updateBoardVisualization(newMoves);
      });

      gameSocket.on('start_slot_spin', ({ selectedMove, color, availableMoves }) => {
        //console.log("⭐ SLOT SPIN EVENT RECEIVED:", { color });
        const setIsSpinning = color === 'white' ? setIsWhiteSpinning : setIsBlackSpinning;
        setIsSpinning(true);

        // Stop any previously playing slot spin sound
        stopSound('SLOT_SPIN');

        // Then play the slot spin sound with a small delay
        setTimeout(() => {
          playSound('SLOT_SPIN', true); // true for looping
        }, 50);

        // Stop timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }

        setFinMoves(prev => ({
          ...prev,
          [color]: selectedMove
        }));
      });

      gameSocket.on('slot_turn_initialized', ({ slotPendingMoves, anarchyMove, uniqueMoveLimit, globalState }) => {
        //console.log("⭐ SLOT TURN INITIALIZED EVENT RECEIVED");
        //console.log('amove',anarchyMove);
        const currentColor = globalState.turn === 'w' ? 'white' : 'black';

        // Reset UI state
        setWhiteSlots(Array(3).fill(null));
        setBlackSlots(Array(3).fill(null));
        whiteSlotsRef.current = null;
        blackSlotsRef.current = null;
        setMoveSquares({});
        setIsWhiteSpinning(false);
        setIsBlackSpinning(false);
        setUniqueMoveLimit(uniqueMoveLimit);

        // Handle sound sequence
        const soundSequence = [];

        // 1. First stop the slot spin sound
        stopSound('SLOT_SPIN');
        //console.log(globalState);

        //console.log(globalState.lastMove);

        // 3. Determine appropriate move sound based on the last move properties
        if (globalState.lastMove) {
          let moveSound = 'MOVE';
          let moveDelay = 10; // Base delay after slot stop sound

          if (globalState.lastMove.san?.includes('#')) {
            moveSound = 'CHECKMATE';
          }
          else if (globalState.lastMove.san?.includes('+')) {
            moveSound = 'CHECK';
          }
          else if (globalState.lastMove.captured) {
            moveSound = 'CAPTURE';
          } else if (globalState.lastMove.san?.includes('O-O')) {
            moveSound = 'CASTLE';
          } else if (globalState.lastMove.promotion) {
            moveSound = 'PROMOTION';
          }

          soundSequence.push({ sound: moveSound, delay: moveDelay });
          //console.log(soundSequence);

        }

        // Play the entire sound sequence
        //console.log(soundSequence);
        //playSequence(soundSequence);


        // Set new slots if any
        if (currentColor === 'white') {
          setWhiteSlots(slotPendingMoves);
          whiteSlotsRef.current = slotPendingMoves;
        } else {
          setBlackSlots(slotPendingMoves);
          blackSlotsRef.current = slotPendingMoves;
        }

        // Update game state
        handleGameState(globalState);
      });





      gameSocket.on('slot_moves_reset', ({ color, slotPendingMoves, anarchyMove, globalState }) => {
        // Update global state
        handleGameState(globalState);

        // Set slots for the specific color
        const setSlots = color === 'white' ? setWhiteSlots : setBlackSlots;

        const newMoves = [...slotPendingMoves];
        if (anarchyMove) {
          newMoves[2] = anarchyMove;
        }
        setSlots(newMoves);

        // Clear visualization
        setMoveSquares({});
        setCustomArrows([]);
        setOptionSquares({});

        // If it's the current player's color, reset selection state
        if (color === playerColor) {
          setSelectedSlot(null);
          setSelectedPiece(null);
        }

        // Optionally visualize moves for both players
        handleMoveSelected(newMoves, color !== playerColor);
      });
    }

    setupSocketHandlers();

    return () => {
      gameSocket.off("connect");
      gameSocket.off("player_disconnected");
      gameSocket.off("player_reconnected");
      gameSocket.off("game_state");
      gameSocket.off("game_update");
      gameSocket.off("game_error");
      gameSocket.off("game_end");
      gameSocket.off("time_update");
      gameSocket.off("draw_offered");
      gameSocket.off("draw_declined");
      gameSocket.off("draw_claimable");
      gameSocket.off("regular_game_offered");
      gameSocket.off("regular_game_accepted");
      gameSocket.off("regular_game_declined");
      gameSocket.off("game_countdown");
      gameSocket.off("slot_turn_initialized");
      gameSocket.off("slot_moves_updated");
      gameSocket.off("slot_moves_reset");
      gameSocket.off("start_slot_spin");


      // Stop all active sounds - access through window.audioElements
      if (window.HTMLAudioElement) {
        Object.keys(window.HTMLAudioElement).forEach(soundName => {
          stopSound(soundName);
        });
      }

    };
  }, [gameSocket, gameId, handleGameState, navigate, playerColor, globalState, handleMoveSelected, startTimerForColor,
    updateBoardVisualization, whiteSlots, blackSlots, isBlackSpinning, isWhiteSpinning, isSpectator, playSound, playWithDelay, stopSound, user.id, playSequence]);

  // Helper functions

  const formatTime = useCallback((seconds) => {
    if (seconds == null) return "--:--";

    // Round to 1 decimal place for display
    seconds = Math.max(0, seconds);
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1).padStart(4, '0');
    return `${mins}:${secs}`;
  }, []);



  // Draw handlers

  const handleDrawOffer = () => {
    if (!gameSocket || !userCanMove || globalState?.status !== "active")
      return;

    gameSocket.emit("offer_draw", { gameId, playerId: user.id });
  };

  const handleDrawResponse = (accept) => {
    if (!gameSocket || globalState?.status !== "active") return;

    gameSocket.emit("respond_to_draw", {
      gameId,

      playerId: user.id,

      accept,
    });

    setDrawOffer(null);


  };

  const handleDrawClaim = () => {
    if (
      !gameSocket ||
      !userCanMove ||
      !canClaimDraw ||
      globalState?.status !== "active"
    )
      return;

    gameSocket.emit("claim_draw", { gameId, playerId: user.id });
  };
  const handleRegularGameOffer = (timeControl) => {
    if (!gameSocket || !userCanMove || regularOffer) return;
    //console.log("offer sent");
    gameSocket.emit("offer_regular_game", {
      gameId,
      playerId: user.id,
      timeControl
    });
    setRegularOffer({ from: user.id, timeControl });
  };

  const handleRegularGameResponse = (accept) => {
    if (!gameSocket || !regularOffer) return;

    gameSocket.emit("respond_to_regular_game", {
      gameId,
      playerId: user.id,
      accept
    });

    setRegularOffer(null);
  };

  const handleAbortGame = useCallback(() => {
    if (
      !gameSocket ||
      globalState?.creator?.id !== user.id ||
      globalState?.status !== "waiting"
    )
      return;

    if (window.confirm("Are you sure you want to abort the game?")) {
      gameSocket.emit("abort_game", { gameId });
      // Navigate back to lobby after abort
      setTimeout(() => navigate("/lobby"), 1000);
    }
  }, [gameSocket, globalState?.creator?.id, globalState?.status, gameId, navigate, user.id]);

  // Player info render

  const getPossibleMoves = useCallback(() => {
    if (!chessRef.current) return [];

    const allMoves = chessRef.current.moves({ verbose: true });
    return allMoves.map(move => ({
      ...move,
      color: chessRef.current.turn(),
      piece: chessRef.current.get(move.from).type,
      fromSquare: move.from
    }));
  }, []);

  const currentPossibleMoves = useMemo(() => {
    if (globalState?.status === 'active') {
      return getPossibleMoves();
    }
    return [];
  }, [globalState?.status, getPossibleMoves]);



  // handlers for GameInfoPanel
  const gameHandlers = {
    // Move handlers
    onSlotClick: (index) => {
      if (globalState?.isAnarchy && index === 2) return;
      const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;
      if (currentSlots[index]) {
        const newMoves = [...currentSlots];
        newMoves[index] = null;
        handleMoveSelected(newMoves);
        setSelectedSlot(null);
      } else {
        setSelectedSlot(index);
      }
    },
    onResetSlots: () => {
      const currentSlots = playerColor === 'white' ? whiteSlots : blackSlots;
      if (globalState?.isAnarchy && currentSlots[2]) {
        const anarchyMove = currentSlots[2];

        // Emit to server to handle reset
        gameSocket?.emit('reset_slot_moves', {
          gameId,
          color: playerColor,
          anarchyMove: globalState.isAnarchy ? anarchyMove : null
        });
      } else {
        // Emit a standard reset
        gameSocket?.emit('reset_slot_moves', {
          gameId,
          color: playerColor,
          anarchyMove: null
        });
      }
    },

    onRandomMove: handleRandomMove,
    onSubmitMove: handleSubmitMoves,
    onScrollToStatus: scrollToStatus,
    // Game control handlers
    onDrawOffer: handleDrawOffer,
    onDrawResponse: handleDrawResponse,
    onDrawClaim: handleDrawClaim,
    onAbortGame: handleAbortGame,
    onResign: () => {
      if (window.confirm("Are you sure you want to resign?")) {
        gameSocket?.emit("resign", { gameId, userId: user.id });
      }
    },
    onFlipBoard: () => setBoardOrientation(current => current === "white" ? "black" : "white"),
    onPositionChange: handlePositionChange,
    onRegularGameOffer: handleRegularGameOffer,

    onRegularGameResponse: handleRegularGameResponse,

    // State for components
    slots: { white: whiteSlots, black: blackSlots },
    isSpinning: { white: isWhiteSpinning, black: isBlackSpinning },
    selectedSlot,
    finMoves,
    showGoldenBorder,
    currentPossibleMoves,
    currentMoveIndex,
    countdownTimer,
    drawOffer,
    canClaimDraw,
    regularOffer,
    isRegularGame,
    isSpectator,
    playerColor,
    uniqueMoveLimit,
    currentTurn,
    status: globalState?.status,
    showUnselected: showUnselectedMoves,
    onToggleUnselected: () => setShowUnselectedMoves(prev => !prev),
    // Messages
    messages: {
      anarchyMessage,
      forcedMoveMessage
    }
  };

  const gameTimers = {
    white: whiteTimer,
    black: blackTimer,
    format: formatTime
  };

  const { renderPlayerInfo, renderGameStatus, renderGameControls } = GameInfoPanel({
    game: globalState,
    user,
    handlers: gameHandlers,
    timers: gameTimers
  });

  // Main render
  return (


    <div className="h-full">
      {globalState?.status === 'waiting' &&
        globalState?.creator?.id === user.id && (
          <div className="mb-4">
            <WaitingGameSuggestion
              game={globalState}
              onSwitchToBotGame={handleSwitchToBotGame}
              onClose={() => setShowWaitingSuggestion(false)}
              inGamePage={true}
            />
          </div>
        )}
      {error ? (
        <div className="text-center p-4">
          <div className={`mb-4 ${woodStyles.text.primary}`}>{error}</div>
          <ThemedButton
            onClick={() => navigate("/lobby")}
            variant="primary"
          >
            Return to Lobby
          </ThemedButton>
        </div>
      ) : !globalState ? (
        <div className="text-center p-4">
          <div className={`mb-4 ${woodStyles.text.secondary}`}>Loading Game State...</div>
          <ThemedButton
            onClick={() => navigate("/lobby")}
            variant="primary"
          >
            Return to Lobby
          </ThemedButton>
        </div>
      ) : (
        <>

          <ChessGameLayout
            position={currentMoveIndex !== -1 && currentMoveIndex !== (globalState?.moves?.length - 1)
              ? viewingRef.current?.fen()
              : globalState.fen ?? chessRef.current.fen()}
            boardOrientation={boardOrientation}
            onSquareClick={currentMoveIndex === (globalState?.moves?.length - 1) ? onSquareClick : undefined}
            onSquareRightClick={currentMoveIndex === (globalState?.moves?.length - 1) ? onSquareRightClick : undefined}
            onPieceDrop={onPieceDrop}
            customSquareStyles={{
              ...moveSquares,
              ...optionSquares,
              ...rightClickedSquares
            }}
            customArrows={customArrows}
            isDraggablePiece={({ piece }) =>
              piece[0] === (playerColor === "white" ? "w" : "b")
            }
            renderPlayerInfo={renderPlayerInfo}
            renderGameStatus={(props) => (
              <div ref={gameStatusRef}>
                {renderGameStatus(props)}
              </div>
            )}
            renderControls={renderGameControls}
            onPromotionPieceSelect={onPromotionPieceSelect}
            promotionToSquare={moveTo}
            showPromotionDialog={!isDragDropPromotion && showPromotionDialog}
            animationDuration={50}
            countdownTimer={countdownTimer}

          />

          {/*<SoundTestComponent />*/}

        </>
      )}
    </div>
  );
};

export default ChessGame;